import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail, BACKEND_URL } from "../lib/api";
import Navbar from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import { Building2, Users, FileText, BookOpen, Calendar, Mail, TrendingUp, ShieldCheck, LogIn, Plus, Trash2, Search, Copy, Check, Loader2, AlertTriangle, CheckCircle2, Clock, CreditCard, Lock, Unlock, KeyRound, Receipt, Printer, X, Edit3, Download } from "lucide-react";
import { toast } from "sonner";

export default function AdminDashboard() {
    const { user, refresh } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [copiedCode, setCopiedCode] = useState("");
    const [usersModal, setUsersModal] = useState(null);
    const [ledgerModal, setLedgerModal] = useState(null);
    const [activationCodesOpen, setActivationCodesOpen] = useState(false);

    const reload = async () => {
        try {
            const { data } = await api.get("/admin/overview");
            setData(data);
        } catch (e) {
            toast.error("Accès refusé");
            navigate("/dashboard");
        } finally { setLoading(false); }
    };

    useEffect(() => {
        if (!user) return;
        if (user.role !== "admin") {
            toast.error("Cette page est réservée à l'administrateur");
            navigate("/dashboard");
            return;
        }
        reload();
        // eslint-disable-next-line
    }, [user]);

    const extend = async (userId) => {
        const days = parseInt(window.prompt("Prolonger l'abonnement de combien de jours ?", "30") || "0", 10);
        if (!days) return;
        try {
            await api.post(`/admin/users/${userId}/extend-subscription?days=${days}`);
            toast.success(`Abonnement prolongé de ${days} jours`);
            reload();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur"); }
    };

    const impersonate = async (userId, email) => {
        if (!window.confirm(`Se connecter en tant que ${email} ?`)) return;
        try {
            await api.post(`/admin/impersonate/${userId}`);
            toast.success("Connecté, redirection…");
            if (refresh) await refresh();
            navigate("/dashboard");
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur"); }
    };

    const removeUser = async (userId, label) => {
        if (!window.confirm(`Supprimer ${label} ? Cette action est irréversible.`)) return;
        try {
            await api.delete(`/admin/users/${userId}`);
            toast.success("Compte supprimé");
            reload();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur"); }
    };

    const toggleAccess = async (userId, label, currentlyActive, status = null) => {
        const action = !currentlyActive ? "ACTIVER" : status === "pending_activation" ? "METTRE EN ATTENTE D'ACTIVATION" : "SUSPENDRE";
        const reason = window.prompt(`${action} l'accès de ${label} ?\n\n(Optionnel) Motif communiqué par e-mail :`, "");
        if (reason === null) return; // user cancelled
        try {
            const { data: res } = await api.post(`/admin/users/${userId}/toggle-access`, {
                active: !currentlyActive,
                reason: reason || null,
                status,
            });
            toast.success(`Accès ${res.action} — e-mail envoyé à ${res.user_email}`);
            reload();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur"); }
    };

    const resetPassword = async (userId, label) => {
        const notify = window.confirm(
            `Réinitialiser le mot de passe de ${label} ?\n\n` +
            `OK = Générer un mot de passe provisoire ET l'envoyer par e-mail à l'utilisateur\n` +
            `Annuler = abandonner\n\n` +
            `Cliquez OK pour continuer.`
        );
        if (!notify) return;
        try {
            const { data: res } = await api.post(`/admin/users/${userId}/reset-password`, {
                notify_user: true,
            });
            // Show the temp password in a copyable prompt so the admin can also share it manually
            window.prompt(
                `✅ Mot de passe provisoire généré pour ${res.user_email}.\n\n` +
                `Un e-mail a été envoyé à l'utilisateur avec ces identifiants.\n` +
                `Vous pouvez aussi le lui communiquer manuellement (copiez ci-dessous) :`,
                res.temp_password
            );
            toast.success(`Mot de passe réinitialisé pour ${res.user_email}`);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
        }
    };

    const openUsersModal = async (establishment) => {
        try {
            const { data } = await api.get(`/admin/establishment/${establishment.establishment_id}/users`);
            setUsersModal({ establishment, users: data });
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Impossible de charger les utilisateurs.");
        }
    };

    const refreshUsersModal = async () => {
        if (!usersModal) return;
        try {
            const { data } = await api.get(`/admin/establishment/${usersModal.establishment.establishment_id}/users`);
            setUsersModal({ ...usersModal, users: data });
        } catch { /* ignore */ }
    };

    const copy = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedCode(text);
            setTimeout(() => setCopiedCode(""), 1800);
        } catch { /* ignore */ }
    };

    const testEmail = async () => {
        const to = window.prompt("Envoyer un e-mail de test à :", user?.email || "");
        if (!to) return;
        try {
            const { data } = await api.post("/admin/test-email", { to });
            toast.success(`E-mail envoyé via ${data.sender} ✓`);
        } catch (e) {
            const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
            toast.error(`Échec : ${msg}`, { duration: 8000 });
        }
    };

    if (loading || !data) {
        return (
            <div className="min-h-screen bg-paper">
                <Navbar />
                <div className="max-w-4xl mx-auto px-6 py-16 text-center text-muted2">Chargement…</div>
            </div>
        );
    }

    const { totals, subscriptions, establishments } = data;
    const filtered = establishments.filter((e) => {
        if (!search.trim()) return true;
        const q = search.toLowerCase();
        return (
            (e.nom_etablissement || "").toLowerCase().includes(q) ||
            (e.responsable_email || "").toLowerCase().includes(q) ||
            (e.invite_code || "").toLowerCase().includes(q)
        );
    });

    return (
        <div className="min-h-screen bg-paper">
            <Navbar />
            <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10" data-testid="admin-dashboard">
                <div className="flex items-center justify-between mb-2">
                    <div className="label-eyebrow text-orange">Administration Profawz</div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <button onClick={() => navigate("/admin/users")} className="btn-outline !text-xs !px-3 !py-2" data-testid="admin-link-users">
                            <Users size={12} /> Tous les utilisateurs
                        </button>
                        <button onClick={() => navigate("/admin/inbox")} className="btn-outline !text-xs !px-3 !py-2" data-testid="admin-link-inbox">
                            <Mail size={12} /> Messages reçus
                        </button>
                        <button onClick={() => navigate("/conformite")} className="btn-outline !text-xs !px-3 !py-2" data-testid="admin-link-compliance">
                            <ShieldCheck size={12} /> RGPD & journal d'audit
                        </button>
                        <span className="pill bg-orange text-white inline-flex items-center gap-1"><ShieldCheck size={11} /> Propriétaire</span>
                    </div>
                </div>
                <h1 className="h-display text-4xl sm:text-5xl font-semibold leading-tight mb-2">
                    Tableau de bord <span className="text-orange">plateforme</span>
                </h1>
                <p className="text-sm text-muted2 mb-8 max-w-2xl">
                    Vue globale de tous les lycées abonnés, utilisateurs, revenus et activité. Vous pouvez vous connecter en tant qu'un utilisateur, prolonger un abonnement ou supprimer un compte.
                </p>

                <div className="panel p-4 mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="admin-email-test-card">
                    <div>
                        <div className="label-eyebrow mb-1">Configuration e-mail (Resend)</div>
                        <p className="text-xs text-muted2">Envoyez un e-mail de test pour valider que la clé Resend et le domaine sont opérationnels.</p>
                    </div>
                    <button onClick={testEmail} className="btn-outline !text-xs !px-4 !py-2 self-start sm:self-auto" data-testid="admin-test-email-button">
                        <Mail size={12} /> Envoyer un e-mail de test
                    </button>
                </div>

                {/* CSV EXPORTS */}
                <div className="panel p-4 mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="admin-export-card">
                    <div>
                        <div className="label-eyebrow mb-1">Export CSV (RGPD / archivage)</div>
                        <p className="text-xs text-muted2">Téléchargez la liste complète des établissements et utilisateurs au format CSV (UTF-8, ouvrable dans Excel).</p>
                    </div>
                    <div className="flex flex-wrap gap-2 self-start sm:self-auto">
                        <a
                            href={`${BACKEND_URL}/api/admin/export/establishments.csv`}
                            className="btn-outline !text-xs !px-3 !py-2"
                            data-testid="admin-export-establishments-button"
                        >
                            <Download size={12} /> Établissements
                        </a>
                        <a
                            href={`${BACKEND_URL}/api/admin/export/users.csv`}
                            className="btn-outline !text-xs !px-3 !py-2"
                            data-testid="admin-export-users-button"
                        >
                            <Download size={12} /> Utilisateurs
                        </a>
                    </div>
                </div>

                <div className="panel p-5 mb-6 bg-emerald-50 border-emerald-200" data-testid="admin-access-mode-card">
                    <div className="flex items-start gap-3">
                        <ShieldCheck size={20} className="text-emerald-700 shrink-0 mt-0.5" />
                        <div className="flex-1">
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                <div>
                                    <div className="label-eyebrow text-emerald-800 mb-1">Mode d'accès actuel</div>
                                    <h3 className="h-display text-lg font-semibold text-emerald-900">Plateforme sur invitation</h3>
                                </div>
                                <button onClick={() => setActivationCodesOpen(true)} className="btn-primary !text-xs !px-3 !py-2 shrink-0" data-testid="admin-open-activation-codes">
                                    <KeyRound size={12} /> Codes d'activation
                                </button>
                            </div>
                            <p className="text-xs text-emerald-900/80 mt-1.5 leading-relaxed">
                                Profawz fonctionne par <strong>codes d'activation Profawz</strong> que vous distribuez manuellement après réception du règlement. Le responsable doit saisir ce code pour créer son compte ; il reçoit alors un code d'établissement (BTS-XXXXXX) à transmettre à ses formateurs et alternants. Pour suspendre l'accès d'un établissement qui n'a pas réglé, utilisez le bouton « Bloquer l'accès » sur sa carte.
                            </p>
                        </div>
                    </div>
                </div>

                {/* KPIs — invitation-only model */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-rule border border-rule mb-6">
                    <Kpi icon={Building2} label="Lycées inscrits" value={totals.establishments} sub={`${totals.users} utilisateurs totaux`} accent="text-orange" />
                    <Kpi icon={CheckCircle2} label="Accès actifs" value={establishments.filter((e) => !e.manual_block).length} sub={`${establishments.filter((e) => e.manual_block).length} bloqué(s)`} accent="text-emerald-700" />
                    <Kpi icon={Users} label="Formateurs" value={establishments.reduce((s, e) => s + (e.formateurs || 0), 0)} sub={`${establishments.reduce((s, e) => s + (e.alternants || 0), 0)} alternants`} accent="text-ink" />
                    <Kpi icon={ShieldCheck} label="Codes distribués" value={establishments.length} sub="1 par établissement" accent="text-ink" />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-rule border border-rule mb-10">
                    <Kpi icon={FileText} label="Bilans créés" value={totals.bilans} />
                    <Kpi icon={BookOpen} label="Cours & TPs" value={totals.courses} />
                    <Kpi icon={Calendar} label="Créneaux agenda" value={totals.agenda_slots} />
                    <Kpi icon={Mail} label="Bulletins envoyés" value={totals.bulletins} />
                </div>

                {/* SEARCH */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                    <h2 className="h-display text-2xl font-semibold">Lycées ({establishments.length})</h2>
                    <div className="relative max-w-sm">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted2" />
                        <input
                            className="field-input !pl-9"
                            placeholder="Rechercher par nom, email, code…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            data-testid="admin-search-input"
                        />
                    </div>
                </div>

                {filtered.length === 0 ? (
                    <div className="panel p-10 text-center text-sm text-muted2">Aucun lycée ne correspond à la recherche.</div>
                ) : (
                    <div className="space-y-3">
                        {filtered.map((e) => (
                            <div key={e.responsable_id} className="panel p-5" data-testid={`admin-estab-${e.responsable_id}`}>
                                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <h3 className="h-display text-lg font-semibold">{e.nom_etablissement}</h3>
                                            <StatusBadge status={e.subscription_status} daysLeft={e.days_left} manualBlock={e.manual_block} establishmentStatus={e.establishment_status} />
                                            {e.deletion_scheduled_for && (
                                                <span className="pill bg-red-50 text-red-700 border border-red-200 inline-flex items-center" data-testid={`admin-deletion-${e.responsable_id}`}>
                                                    <Trash2 size={11} className="mr-1" /> Fermeture demandée · {new Date(e.deletion_scheduled_for).toLocaleDateString("fr-FR")}
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-xs font-mono text-muted2 mt-1 flex flex-wrap gap-3">
                                            <span>{e.responsable_name || "—"} · {e.responsable_email}</span>
                                            {e.responsable_phone && <span>{e.responsable_phone}</span>}
                                        </div>
                                        <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-ink/70">
                                            <span className="inline-flex items-center gap-1"><Users size={11} /> {e.formateurs} formateur(s) · {e.alternants} alternant(s)</span>
                                            <button onClick={() => copy(e.invite_code)} className="inline-flex items-center gap-1 hover:text-orange font-mono" data-testid={`admin-copy-code-${e.responsable_id}`}>
                                                {copiedCode === e.invite_code ? <Check size={11} className="text-emerald-600" /> : <Copy size={11} />} Code : <span className="font-bold">{e.invite_code}</span>
                                            </button>
                                            {e.subscription_expires_at && (
                                                <span className="inline-flex items-center gap-1">
                                                    <Clock size={11} /> Expire le {new Date(e.subscription_expires_at).toLocaleDateString("fr-FR")}
                                                    {e.days_left != null && ` (${e.days_left}j)`}
                                                </span>
                                            )}
                                        </div>
                                        {e.last_paid_at ? (
                                            <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 px-2 py-1 inline-flex items-center" data-testid={`admin-estab-last-payment-${e.responsable_id}`}>
                                                <Receipt size={11} />
                                                <span>
                                                    Dernier règlement : <strong>{new Date(e.last_paid_at).toLocaleDateString("fr-FR")}</strong>
                                                    {typeof e.last_amount === "number" && <> · <strong>{e.last_amount.toFixed(2)} {e.last_currency || "EUR"}</strong></>}
                                                    {e.last_motif && <> · {e.last_motif}</>}
                                                    {e.payment_records_count > 1 && <> · <em>{e.payment_records_count} règlements au total</em></>}
                                                </span>
                                            </div>
                                        ) : (
                                            <div className="mt-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 px-2 py-1 inline-flex items-center gap-1" data-testid={`admin-estab-no-payment-${e.responsable_id}`}>
                                                <AlertTriangle size={11} /> Aucun règlement enregistré
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex flex-wrap gap-2 shrink-0">
                                        <button onClick={() => setLedgerModal({ establishment: e })} className="btn-outline !text-xs !px-3 !py-2 !text-emerald-700 !border-emerald-300 hover:!bg-emerald-50" title="Carnet de règlements (factures, conventions, motifs)" data-testid={`admin-ledger-${e.responsable_id}`}>
                                            <Receipt size={12} /> Carnet ({e.payment_records_count || 0})
                                        </button>
                                        <button onClick={() => openUsersModal(e)} className="btn-outline !text-xs !px-3 !py-2" title="Voir tous les utilisateurs (formateurs + alternants)" data-testid={`admin-view-users-${e.responsable_id}`}>
                                            <Users size={12} /> Utilisateurs ({e.users_total})
                                        </button>
                                        <button onClick={() => impersonate(e.responsable_id, e.responsable_email)} className="btn-outline !text-xs !px-3 !py-2" title="Se connecter en tant que" data-testid={`admin-impersonate-${e.responsable_id}`}>
                                            <LogIn size={12} /> Se connecter
                                        </button>
                                        <button onClick={() => extend(e.responsable_id)} className="btn-outline !text-xs !px-3 !py-2" title="Prolonger l'abonnement" data-testid={`admin-extend-${e.responsable_id}`}>
                                            <Plus size={12} /> Offrir des jours
                                        </button>
                                        <button
                                            onClick={() => resetPassword(e.responsable_id, e.responsable_email)}
                                            className="btn-outline !text-xs !px-3 !py-2 !text-blue-700 !border-blue-300 hover:!bg-blue-50"
                                            title="Générer un mot de passe provisoire (envoyé par e-mail)"
                                            data-testid={`admin-reset-pwd-${e.responsable_id}`}
                                        >
                                            <KeyRound size={12} /> Reset MDP
                                        </button>
                                        {!e.subscription_active || e.manual_block ? (
                                            <button
                                                onClick={() => toggleAccess(e.responsable_id, e.responsable_email, false)}
                                                className="btn-outline !text-xs !px-3 !py-2 !text-emerald-700 !border-emerald-300 hover:!bg-emerald-50"
                                                title="Débloquer l'accès de TOUT l'établissement (responsable + formateurs + alternants)"
                                                data-testid={`admin-unblock-${e.responsable_id}`}
                                            >
                                                <Unlock size={12} /> {e.establishment_status === "pending_activation" ? "Activer" : "Débloquer"}
                                            </button>
                                        ) : (
                                            <>
                                            <button
                                                onClick={() => toggleAccess(e.responsable_id, e.responsable_email, true, "pending_activation")}
                                                className="btn-outline !text-xs !px-3 !py-2 !text-amber-700 !border-amber-300 hover:!bg-amber-50"
                                                title="Mettre l'établissement en attente d'activation (virement non reçu)"
                                                data-testid={`admin-pending-${e.responsable_id}`}
                                            >
                                                <Clock size={12} /> En attente
                                            </button>
                                            <button
                                                onClick={() => toggleAccess(e.responsable_id, e.responsable_email, true)}
                                                className="btn-outline !text-xs !px-3 !py-2 !text-amber-700 !border-amber-300 hover:!bg-amber-50"
                                                title="Bloquer l'accès de TOUT l'établissement (responsable + formateurs + alternants)"
                                                data-testid={`admin-block-${e.responsable_id}`}
                                            >
                                                <Lock size={12} /> Suspendre
                                            </button>
                                            </>
                                        )}
                                        <button onClick={() => removeUser(e.responsable_id, e.responsable_email)} className="btn-outline !text-xs !px-3 !py-2 !text-destructive !border-destructive/30 hover:!bg-destructive/5" title="Supprimer le lycée" data-testid={`admin-delete-${e.responsable_id}`}>
                                            <Trash2 size={12} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* DANGER ZONE — reset full platform */}
                <DangerZone reload={reload} />
            </main>

            {/* USERS MODAL — view all formateurs/alternants of an establishment */}
            {usersModal && (
                <div className="fixed inset-0 z-50 bg-ink/70 flex items-center justify-center p-4" data-testid="admin-users-modal" onClick={() => setUsersModal(null)}>
                    <div className="bg-paper max-w-3xl w-full max-h-[85vh] flex flex-col border border-rule" onClick={(ev) => ev.stopPropagation()}>
                        <div className="p-5 border-b border-rule flex items-start justify-between gap-3">
                            <div>
                                <div className="label-eyebrow">Utilisateurs de l'établissement</div>
                                <h2 className="h-display text-xl font-semibold mt-1">{usersModal.establishment.nom_etablissement}</h2>
                                <div className="text-xs font-mono text-muted2 mt-1">Code : {usersModal.establishment.invite_code} · {usersModal.users.length} utilisateur(s)</div>
                            </div>
                            <button onClick={() => setUsersModal(null)} className="p-1.5 hover:bg-ink/5" aria-label="Fermer">×</button>
                        </div>
                        <div className="flex-1 overflow-y-auto scroll-visible">
                            {usersModal.users.length === 0 ? (
                                <div className="p-10 text-center text-xs text-muted2 font-mono">Aucun utilisateur dans cet établissement (à part le responsable).</div>
                            ) : (
                                usersModal.users.map((u) => (
                                    <div key={u.id} className="p-4 border-b border-rule flex flex-col sm:flex-row sm:items-center gap-3 hover:bg-surface" data-testid={`admin-user-row-${u.id}`}>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className={`pill !text-[10px] ${u.role === 'responsable' ? 'bg-orange text-white' : u.role === 'formateur' ? 'bg-ink text-white' : 'bg-orange-50 text-orange-700 border border-orange-200'}`}>{u.role}</span>
                                                <span className="font-semibold text-sm truncate">{u.prenom || ''} {u.nom || ''}</span>
                                            </div>
                                            <div className="text-xs font-mono text-muted2 mt-1 flex flex-wrap gap-3">
                                                <span>{u.email}</span>
                                                {u.phone && <span>{u.phone}</span>}
                                                {u.tutor_code && <span className="text-orange">Tuteur : {u.tutor_code}</span>}
                                            </div>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            <button
                                                onClick={() => resetPassword(u.id, u.email)}
                                                className="btn-outline !text-xs !px-3 !py-1.5 !text-blue-700 !border-blue-300 hover:!bg-blue-50"
                                                title="Réinitialiser le mot de passe"
                                                data-testid={`admin-user-reset-${u.id}`}
                                            >
                                                <KeyRound size={11} /> Reset MDP
                                            </button>
                                            <button
                                                onClick={async () => {
                                                    if (!window.confirm(`Supprimer définitivement ${u.email} ?\nTous ses bilans seront aussi effacés. Action IRRÉVERSIBLE.`)) return;
                                                    try {
                                                        await api.delete(`/admin/users/${u.id}`);
                                                        toast.success("Compte supprimé");
                                                        await refreshUsersModal();
                                                        reload();
                                                    } catch (er) { toast.error(formatApiErrorDetail(er.response?.data?.detail) || "Erreur"); }
                                                }}
                                                className="btn-outline !text-xs !px-3 !py-1.5 !text-destructive !border-destructive/30 hover:!bg-destructive/5"
                                                title="Supprimer ce compte"
                                                data-testid={`admin-user-delete-${u.id}`}
                                            >
                                                <Trash2 size={11} />
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* LEDGER MODAL — Carnet de règlements */}
            {ledgerModal && (
                <PaymentLedgerModal
                    establishment={ledgerModal.establishment}
                    onClose={() => setLedgerModal(null)}
                    onChanged={() => { reload(); }}
                />
            )}

            {activationCodesOpen && (
                <ActivationCodesModal onClose={() => setActivationCodesOpen(false)} />
            )}
        </div>
    );
}

function Kpi({ icon: Icon, label, value, sub, accent }) {
    return (
        <div className="bg-paper p-5">
            <div className="flex items-center gap-2 label-eyebrow">
                <Icon size={12} /> {label}
            </div>
            <div className={`h-display text-2xl font-bold mt-2 ${accent || ""}`}>{value}</div>
            {sub && <div className="text-[11px] font-mono text-muted2 mt-0.5">{sub}</div>}
        </div>
    );
}

function StatusBadge({ status, daysLeft, manualBlock, establishmentStatus }) {
    if (establishmentStatus === "pending_activation") return <span className="pill bg-amber-50 text-amber-800 border border-amber-300 inline-flex items-center" data-testid="status-pending"><Clock size={11} className="mr-1" /> En attente d'activation</span>;
    if (establishmentStatus === "suspended") return <span className="pill bg-red-600 text-white inline-flex items-center" data-testid="status-suspended"><Lock size={11} className="mr-1" /> Suspendu</span>;
    if (manualBlock) return <span className="pill bg-red-600 text-white inline-flex items-center" data-testid="status-blocked"><Lock size={11} className="mr-1" /> Bloqué</span>;
    if (status === "paid") return <span className="pill bg-emerald-50 text-emerald-700 border border-emerald-200 inline-flex items-center"><CheckCircle2 size={11} className="mr-1" /> Actif</span>;
    if (status === "trial") return <span className="pill bg-emerald-50 text-emerald-700 border border-emerald-200 inline-flex items-center"><CheckCircle2 size={11} className="mr-1" /> Actif</span>;
    if (status === "active") return <span className="pill bg-ink text-white inline-flex items-center">Actif</span>;
    return <span className="pill bg-red-50 text-red-700 border border-red-200 inline-flex items-center"><AlertTriangle size={11} className="mr-1" /> Suspendu</span>;
}

function DangerZone({ reload }) {
    const [busy, setBusy] = useState(false);
    const [step, setStep] = useState(0); // 0 closed, 1 first warning, 2 final input

    const handleWipe = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/admin/wipe-data");
            const total = Object.values(data.summary || {}).reduce((s, n) => (typeof n === "number" ? s + n : s), 0);
            toast.success(`✅ Plateforme remise à zéro · ${total} enregistrement(s) supprimé(s).`);
            setStep(0);
            reload();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
        } finally { setBusy(false); }
    };

    return (
        <>
            <div className="panel p-5 mt-10 border-l-4 border-l-destructive bg-red-50/40" data-testid="admin-danger-zone">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex-1">
                        <div className="label-eyebrow text-destructive mb-1 inline-flex items-center gap-2">
                            <AlertTriangle size={12} /> Zone de danger
                        </div>
                        <h3 className="h-display text-lg font-semibold">Remettre la plateforme à zéro</h3>
                        <p className="text-xs text-muted2 mt-1.5 max-w-2xl leading-relaxed">
                            Supprime <strong>toutes les données de test</strong> : utilisateurs (sauf vous), lycées, cours/TPs, bilans, RDV agenda, bulletins, messages internes, fichiers uploadés, paiements, messages de contact, tentatives de connexion. La configuration des tarifs et des codes promo est conservée.
                        </p>
                    </div>
                    <button
                        onClick={() => setStep(1)}
                        disabled={busy}
                        className="btn-outline !text-xs !px-4 !py-2 !text-destructive !border-destructive/40 hover:!bg-destructive hover:!text-white self-start sm:self-auto"
                        data-testid="admin-wipe-trigger"
                    >
                        <Trash2 size={12} /> Tout réinitialiser
                    </button>
                </div>
            </div>

            {step > 0 && (
                <div className="fixed inset-0 z-50 bg-ink/80 flex items-center justify-center p-4" onClick={() => !busy && setStep(0)} data-testid="admin-wipe-modal">
                    <div className="bg-paper max-w-lg w-full border-2 border-destructive" onClick={(ev) => ev.stopPropagation()}>
                        <div className="p-5 bg-destructive text-white flex items-center gap-2">
                            <AlertTriangle size={18} />
                            <h2 className="h-display text-lg font-semibold">Action IRRÉVERSIBLE</h2>
                        </div>
                        <div className="p-6">
                            {step === 1 && (
                                <>
                                    <p className="text-sm text-ink mb-4 leading-relaxed">
                                        Vous êtes sur le point de <strong>supprimer toutes les données opérationnelles</strong> de Profawz :
                                    </p>
                                    <ul className="text-xs text-ink/70 space-y-1 list-disc list-inside mb-6">
                                        <li>Tous les comptes (sauf le vôtre)</li>
                                        <li>Tous les lycées et leurs bilans</li>
                                        <li>Tous les cours / TPs / évaluations</li>
                                        <li>Tous les RDV agenda et bulletins</li>
                                        <li>Tous les messages internes et fichiers</li>
                                        <li>Tous les messages reçus du formulaire contact</li>
                                    </ul>
                                    <p className="text-xs text-muted2 mb-6">
                                        ✅ Sont conservés : votre compte admin, les tarifs configurés, les codes promo Stripe.
                                    </p>
                                    <div className="flex gap-2 justify-end">
                                        <button onClick={() => setStep(0)} className="btn-outline !text-xs !px-4 !py-2" data-testid="admin-wipe-cancel">Annuler</button>
                                        <button onClick={() => setStep(2)} className="btn-primary !bg-destructive !border-destructive !text-xs !px-4 !py-2" data-testid="admin-wipe-step2">
                                            Je comprends, continuer
                                        </button>
                                    </div>
                                </>
                            )}
                            {step === 2 && (
                                <DangerConfirm onCancel={() => setStep(0)} onConfirm={handleWipe} busy={busy} />
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

function DangerConfirm({ onCancel, onConfirm, busy }) {
    const [val, setVal] = useState("");
    return (
        <>
            <p className="text-sm text-ink mb-3">
                Pour confirmer, tapez <strong className="font-mono text-destructive">SUPPRIMER</strong> dans le champ ci-dessous :
            </p>
            <input
                autoFocus
                value={val}
                onChange={(e) => setVal(e.target.value)}
                className="field-input font-mono uppercase tracking-wider mb-4"
                placeholder="SUPPRIMER"
                data-testid="admin-wipe-confirm-input"
            />
            <div className="flex gap-2 justify-end">
                <button onClick={onCancel} disabled={busy} className="btn-outline !text-xs !px-4 !py-2" data-testid="admin-wipe-final-cancel">Annuler</button>
                <button
                    onClick={onConfirm}
                    disabled={busy || val.trim().toUpperCase() !== "SUPPRIMER"}
                    className="btn-primary !bg-destructive !border-destructive !text-xs !px-4 !py-2"
                    data-testid="admin-wipe-final-confirm"
                >
                    {busy ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                    {busy ? "Suppression…" : "Tout supprimer maintenant"}
                </button>
            </div>
        </>
    );
}

function FreeModeCard() {
    const [enabled, setEnabled] = useState(null);
    const [busy, setBusy] = useState(false);
    const [askDisable, setAskDisable] = useState(false);

    useEffect(() => {
        api.get("/admin/feature-flags").then(({ data }) => setEnabled(!!data.free_mode_enabled)).catch(() => setEnabled(false));
    }, []);

    const enable = async () => {
        if (!window.confirm("Activer le MODE GRATUIT pour TOUS ?\n\nTous les responsables (existants + futurs) auront un accès illimité jusqu'en 2099-12-31.\nLes pages /subscribe sont automatiquement masquées.")) return;
        setBusy(true);
        try {
            const { data } = await api.post("/admin/feature-flags", { free_mode_enabled: true });
            setEnabled(!!data.free_mode_enabled);
            toast.success(`✅ Mode gratuit ACTIVÉ — ${data.affected_responsables} responsable(s) prolongé(s).`);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
        } finally { setBusy(false); }
    };

    const disable = async (apply_to_existing) => {
        setBusy(true);
        setAskDisable(false);
        try {
            const { data } = await api.post("/admin/feature-flags", { free_mode_enabled: false, apply_to_existing });
            setEnabled(!!data.free_mode_enabled);
            const msgs = {
                keep_lifetime: `Mode gratuit désactivé pour les nouveaux. Les ${data.affected_responsables} bêta-testeurs gardent leur accès gratuit à vie.`,
                trial_30_days: `✅ Mode gratuit désactivé. ${data.affected_responsables} bêta-testeur(s) basculé(s) en essai 30 jours sans CB.`,
                block_immediately: `⚠️ Accès révoqué pour ${data.affected_responsables} bêta-testeur(s). Ils devront souscrire pour continuer.`,
            };
            toast.success(msgs[apply_to_existing] || "Mode gratuit désactivé");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
        } finally { setBusy(false); }
    };

    if (enabled === null) return null;
    return (
        <>
            <div className={`panel p-4 mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ${enabled ? "bg-emerald-50 border-l-4 border-l-emerald-500" : ""}`} data-testid="admin-free-mode-card">
                <div>
                    <div className="label-eyebrow mb-1 inline-flex items-center gap-2">
                        Mode d'accès global
                        {enabled
                            ? <span className="pill bg-emerald-600 text-white !text-[10px]">GRATUIT POUR TOUS</span>
                            : <span className="pill bg-ink text-white !text-[10px]">PAIEMENT STRIPE</span>}
                    </div>
                    <p className="text-xs text-muted2 max-w-xl">
                        {enabled
                            ? "Tous les responsables ont un accès illimité (essai désactivé, plus de gating Stripe). Idéal pour une période de bêta ou de lancement."
                            : "Les responsables s'inscrivent avec un essai de 30 jours sans CB, puis souscrivent à un plan Stripe pour continuer."}
                    </p>
                </div>
                <button
                    onClick={enabled ? () => setAskDisable(true) : enable}
                    disabled={busy}
                    className={`!text-xs !px-4 !py-2 self-start sm:self-auto ${enabled ? "btn-outline !text-amber-700 !border-amber-300 hover:!bg-amber-50" : "btn-primary"}`}
                    data-testid="admin-free-mode-toggle"
                >
                    {busy ? <Loader2 size={12} className="animate-spin" /> : null}
                    {enabled ? "Désactiver le mode gratuit" : "Activer le mode gratuit pour tous"}
                </button>
            </div>

            {askDisable && (
                <div className="fixed inset-0 z-50 bg-ink/70 flex items-center justify-center p-4" data-testid="admin-disable-freemode-modal" onClick={() => setAskDisable(false)}>
                    <div className="bg-paper max-w-lg w-full border border-rule" onClick={(ev) => ev.stopPropagation()}>
                        <div className="p-5 border-b border-rule">
                            <div className="label-eyebrow text-amber-700">Désactiver le mode gratuit</div>
                            <h2 className="h-display text-xl font-semibold mt-1">Que faire des bêta-testeurs ?</h2>
                            <p className="text-xs text-muted2 mt-2">
                                Les responsables actuellement en accès gratuit (jusqu'en 2099) doivent recevoir une nouvelle date d'expiration. Choisis la transition :
                            </p>
                        </div>
                        <div className="p-5 space-y-3">
                            <button
                                onClick={() => disable("keep_lifetime")}
                                className="w-full text-left p-4 border border-rule hover:bg-emerald-50 hover:border-emerald-300 transition-colors"
                                data-testid="freemode-keep-lifetime"
                            >
                                <div className="font-semibold text-sm flex items-center gap-2">
                                    <CheckCircle2 size={14} className="text-emerald-600" /> Les laisser gratuits à vie (cadeau de fidélité)
                                </div>
                                <div className="text-xs text-muted2 mt-1">Recommandé. Tes premiers utilisateurs gardent leur accès gratuit. Seuls les nouveaux inscrits verront Stripe.</div>
                            </button>
                            <button
                                onClick={() => disable("trial_30_days")}
                                className="w-full text-left p-4 border border-rule hover:bg-amber-50 hover:border-amber-300 transition-colors"
                                data-testid="freemode-trial-30"
                            >
                                <div className="font-semibold text-sm flex items-center gap-2">
                                    <Clock size={14} className="text-amber-700" /> Les passer en essai 30 jours sans CB
                                </div>
                                <div className="text-xs text-muted2 mt-1">Ils continuent gratuitement encore 30 jours, reçoivent les e-mails de relance J-7/J-3/J-1, puis devront souscrire.</div>
                            </button>
                            <button
                                onClick={() => {
                                    if (window.confirm("⚠️ ACTION IRRÉVERSIBLE.\n\nTous les bêta-testeurs vont perdre leur accès IMMÉDIATEMENT et devront souscrire à un plan Stripe pour se reconnecter.\n\nConfirmer ?")) disable("block_immediately");
                                }}
                                className="w-full text-left p-4 border border-rule hover:bg-red-50 hover:border-red-300 transition-colors"
                                data-testid="freemode-block-now"
                            >
                                <div className="font-semibold text-sm flex items-center gap-2">
                                    <Lock size={14} className="text-destructive" /> Bloquer leur accès immédiatement
                                </div>
                                <div className="text-xs text-muted2 mt-1">Risque de churn massif. À utiliser seulement si tu changes complètement de stratégie.</div>
                            </button>
                            <button onClick={() => setAskDisable(false)} className="text-xs font-mono text-muted2 hover:text-ink mx-auto block mt-3" data-testid="freemode-cancel">Annuler</button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

function PaymentLedgerModal({ establishment, onClose, onChanged }) {
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // null | "new" | record object
    const [saving, setSaving] = useState(false);

    const reload = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/payment-records?establishment_id=${establishment.establishment_id}`);
            setRecords(data || []);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { reload(); /* eslint-disable-next-line */ }, [establishment.establishment_id]);

    const remove = async (record_id) => {
        if (!window.confirm("Supprimer ce règlement du carnet ? Action irréversible.")) return;
        try {
            await api.delete(`/admin/payment-records/${record_id}`);
            toast.success("Règlement supprimé");
            await reload();
            onChanged?.();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
        }
    };

    const save = async (form) => {
        setSaving(true);
        try {
            const payload = {
                amount: form.amount === "" ? null : parseFloat(form.amount),
                currency: form.currency || "EUR",
                motif: form.motif,
                payment_method: form.payment_method || null,
                period_start: form.period_start || null,
                period_end: form.period_end || null,
                paid_at: form.paid_at || null,
                notes: form.notes || null,
            };
            if (editing === "new") {
                await api.post("/admin/payment-records", { establishment_id: establishment.establishment_id, ...payload });
                toast.success("Règlement enregistré dans le carnet");
            } else {
                await api.put(`/admin/payment-records/${editing.id}`, payload);
                toast.success("Règlement mis à jour");
            }
            setEditing(null);
            await reload();
            onChanged?.();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
        } finally {
            setSaving(false);
        }
    };

    const totalAmount = records.reduce((s, r) => s + (typeof r.amount === "number" ? r.amount : 0), 0);

    const printConvention = (r) => {
        // Open a print-friendly tab so the admin can save it as PDF (Ctrl+P).
        // We build the HTML as a Blob URL instead of using document.write — Blob
        // URLs are sandboxed per-origin and don't carry the XSS footguns of
        // dynamic document.write (no current document's parser is hijacked).
        const dateFr = (iso) => iso ? new Date(iso).toLocaleDateString("fr-FR") : "—";
        const amount = typeof r.amount === "number" ? `${r.amount.toFixed(2)} ${r.currency || "EUR"}` : "—";
        const html = `<!doctype html><html lang="fr"><head><meta charset="utf-8" />
<title>Convention Profawz · ${establishment.nom_etablissement || ""}</title>
<style>
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 720px; margin: 40px auto; padding: 0 24px; color: #0a0a0a; line-height: 1.55; }
  h1 { color: #15A36A; font-size: 28px; margin-bottom: 4px; }
  .muted { color: #525252; font-size: 13px; }
  .section { margin-top: 28px; padding-top: 18px; border-top: 1px solid #e7e5e4; }
  table { width: 100%; border-collapse: collapse; margin-top: 8px; font-size: 14px; }
  th, td { text-align: left; padding: 8px 0; border-bottom: 1px solid #f5f5f4; vertical-align: top; }
  th { width: 36%; color: #525252; font-weight: 600; }
  .footer { margin-top: 50px; font-size: 12px; color: #737373; }
  .signature { margin-top: 40px; display: flex; gap: 40px; }
  .signature > div { flex: 1; border-top: 1px solid #0a0a0a; padding-top: 6px; font-size: 12px; color: #525252; }
  @media print { body { margin: 20px auto; } button { display: none; } }
</style></head><body>
<div style="display:flex;align-items:center;gap:12px">
  <div style="width:36px;height:36px;background:#15A36A;color:#fff;display:flex;align-items:center;justify-content:center;font-weight:bold;font-size:18px">P</div>
  <div>
    <h1 style="margin:0;color:#15A36A;font-size:24px">Profawz</h1>
    <div class="muted">Convention de mise à disposition</div>
  </div>
</div>

<div class="section">
  <h2 style="font-size:18px;margin:0 0 6px">Récapitulatif du règlement</h2>
  <div class="muted">Référence : ${r.id}</div>
  <table>
    <tr><th>Établissement</th><td><strong>${establishment.nom_etablissement || "—"}</strong></td></tr>
    <tr><th>Code d'établissement</th><td><code style="background:#f5f5f4;padding:2px 6px">${establishment.invite_code || r.invite_code_snapshot || "—"}</code></td></tr>
    <tr><th>Responsable</th><td>${establishment.responsable_name || ""} · ${establishment.responsable_email || r.responsable_email || ""}</td></tr>
    <tr><th>Date du règlement</th><td>${dateFr(r.paid_at)}</td></tr>
    <tr><th>Montant</th><td><strong>${amount}</strong></td></tr>
    <tr><th>Motif</th><td>${r.motif || "—"}</td></tr>
    <tr><th>Mode de paiement</th><td>${r.payment_method || "—"}</td></tr>
    <tr><th>Période couverte</th><td>${r.period_start || r.period_end ? `du ${dateFr(r.period_start)} au ${dateFr(r.period_end)}` : "—"}</td></tr>
    ${r.notes ? `<tr><th>Notes</th><td style="white-space:pre-wrap">${r.notes.replace(/[<>]/g, '')}</td></tr>` : ""}
  </table>
</div>

<div class="section">
  <h2 style="font-size:18px;margin:0 0 6px">Conditions</h2>
  <p style="font-size:13px">Le règlement ci-dessus active l'accès à la plateforme Profawz pour l'établissement désigné, conformément aux <a href="https://profawz.fr/cgv">Conditions Générales de Vente</a> en vigueur. L'accès est suspensible à tout moment en cas de manquement aux conditions, après notification par e-mail au responsable.</p>
  <p style="font-size:13px">Les données personnelles collectées sont traitées conformément à la <a href="https://profawz.fr/confidentialite">Politique de confidentialité</a> et au RGPD (UE 2016/679).</p>
</div>

<div class="signature">
  <div>Signature du responsable<br/>(${establishment.responsable_email || ""})</div>
  <div>Pour Profawz<br/>Imane Akmoun</div>
</div>

<div class="footer">
  Document généré le ${new Date().toLocaleDateString("fr-FR")} · Profawz · SIRET 991 829 474 00014 · contact@profawz.fr
</div>

<button onclick="window.print()" style="position:fixed;top:10px;right:10px;padding:10px 16px;background:#15A36A;color:#fff;border:none;cursor:pointer;font-weight:bold">Imprimer / Enregistrer en PDF</button>
</body></html>`;
        const blob = new Blob([html], { type: "text/html;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const w = window.open(url, "_blank", "noopener,noreferrer");
        if (!w) {
            URL.revokeObjectURL(url);
            toast.error("Veuillez autoriser les pop-ups pour imprimer la convention.");
            return;
        }
        // Free the Blob URL after the new tab has loaded it
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
    };

    return (
        <div className="fixed inset-0 z-50 bg-ink/70 flex items-center justify-center p-4" data-testid="payment-ledger-modal" onClick={onClose}>
            <div className="bg-paper max-w-3xl w-full max-h-[90vh] flex flex-col border border-rule" onClick={(ev) => ev.stopPropagation()}>
                <div className="p-5 border-b border-rule flex items-start justify-between gap-3">
                    <div>
                        <div className="label-eyebrow">Carnet de règlements</div>
                        <h2 className="h-display text-xl font-semibold mt-1">{establishment.nom_etablissement}</h2>
                        <div className="text-xs font-mono text-muted2 mt-1">
                            Code : {establishment.invite_code} · {records.length} règlement(s)
                            {records.length > 0 && totalAmount > 0 && (
                                <> · Total : <strong className="text-emerald-700">{totalAmount.toFixed(2)} EUR</strong></>
                            )}
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1.5 hover:bg-ink/5" data-testid="ledger-close" aria-label="Fermer"><X size={18} /></button>
                </div>

                <div className="p-4 border-b border-rule bg-surface flex flex-wrap gap-2 items-center justify-between">
                    <p className="text-xs text-muted2">Notez ici chaque règlement reçu hors plateforme : convention initiale, renouvellement annuel, etc.</p>
                    <button onClick={() => setEditing("new")} className="btn-primary !text-xs !px-3 !py-2" data-testid="ledger-add-button" disabled={editing !== null}>
                        <Plus size={12} /> Ajouter un règlement
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto scroll-visible">
                    {editing && <LedgerForm record={editing === "new" ? null : editing} onCancel={() => setEditing(null)} onSave={save} saving={saving} />}

                    {loading ? (
                        <div className="p-10 text-center text-xs text-muted2 font-mono">Chargement…</div>
                    ) : records.length === 0 ? (
                        <div className="p-10 text-center text-xs text-muted2 font-mono" data-testid="ledger-empty">Aucun règlement enregistré pour cet établissement.</div>
                    ) : records.map((r) => (
                        <div key={r.id} className="p-4 border-b border-rule hover:bg-surface" data-testid={`ledger-record-${r.id}`}>
                            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                                <div className="flex-1 min-w-0">
                                    <div className="flex flex-wrap items-baseline gap-2">
                                        <span className="font-semibold text-sm">{r.motif}</span>
                                        {typeof r.amount === "number" && (
                                            <span className="pill bg-emerald-50 text-emerald-700 border border-emerald-200 !text-[11px]">
                                                {r.amount.toFixed(2)} {r.currency || "EUR"}
                                            </span>
                                        )}
                                        {r.payment_method && (
                                            <span className="text-[11px] font-mono text-muted2">via {r.payment_method}</span>
                                        )}
                                    </div>
                                    <div className="text-[11px] font-mono text-muted2 mt-1 flex flex-wrap gap-3">
                                        <span>Reçu le <strong>{r.paid_at ? new Date(r.paid_at).toLocaleDateString("fr-FR") : "—"}</strong></span>
                                        {(r.period_start || r.period_end) && (
                                            <span>Période : {r.period_start ? new Date(r.period_start).toLocaleDateString("fr-FR") : "?"} → {r.period_end ? new Date(r.period_end).toLocaleDateString("fr-FR") : "?"}</span>
                                        )}
                                    </div>
                                    {r.notes && <p className="text-xs text-ink/70 mt-2 whitespace-pre-wrap">{r.notes}</p>}
                                </div>
                                <div className="flex flex-wrap gap-1.5 shrink-0">
                                    <button onClick={() => printConvention(r)} className="btn-outline !text-xs !px-2 !py-1.5" title="Imprimer / Enregistrer en PDF" data-testid={`ledger-print-${r.id}`}>
                                        <Printer size={12} />
                                    </button>
                                    <button onClick={() => setEditing(r)} className="btn-outline !text-xs !px-2 !py-1.5" title="Modifier" data-testid={`ledger-edit-${r.id}`} disabled={editing !== null}>
                                        <Edit3 size={12} />
                                    </button>
                                    <button onClick={() => remove(r.id)} className="btn-outline !text-xs !px-2 !py-1.5 !text-destructive !border-destructive/30 hover:!bg-destructive/5" title="Supprimer" data-testid={`ledger-delete-${r.id}`}>
                                        <Trash2 size={12} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function LedgerForm({ record, onCancel, onSave, saving }) {
    const isNew = !record;
    const today = new Date().toISOString().slice(0, 10);
    const [form, setForm] = useState({
        amount: record?.amount ?? "",
        currency: record?.currency || "EUR",
        motif: record?.motif || "Règlement reçu",
        payment_method: record?.payment_method || "",
        period_start: record?.period_start ? record.period_start.slice(0, 10) : "",
        period_end: record?.period_end ? record.period_end.slice(0, 10) : "",
        paid_at: record?.paid_at ? record.paid_at.slice(0, 10) : today,
        notes: record?.notes || "",
    });
    const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const submit = (e) => {
        e.preventDefault();
        if (!form.motif.trim()) { toast.error("Le motif est obligatoire."); return; }
        onSave(form);
    };

    return (
        <form onSubmit={submit} className="p-5 border-b-2 border-orange/40 bg-orange/5 space-y-3" data-testid="ledger-form">
            <div className="flex items-center justify-between mb-1">
                <h3 className="h-display text-base font-semibold">{isNew ? "Nouveau règlement" : "Modifier le règlement"}</h3>
                <button type="button" onClick={onCancel} className="text-xs text-muted2 hover:text-ink" data-testid="ledger-form-cancel">× Annuler</button>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
                <div>
                    <label className="label-eyebrow mb-1 block">Date du règlement *</label>
                    <input type="date" value={form.paid_at} onChange={(e) => set("paid_at", e.target.value)} className="field-input !py-2" required data-testid="ledger-form-paid-at" />
                </div>
                <div>
                    <label className="label-eyebrow mb-1 block">Motif *</label>
                    <input type="text" value={form.motif} onChange={(e) => set("motif", e.target.value)} className="field-input !py-2" placeholder="ex : Renouvellement annuel" required maxLength={200} data-testid="ledger-form-motif" />
                </div>
                <div>
                    <label className="label-eyebrow mb-1 block">Montant</label>
                    <div className="flex gap-2">
                        <input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => set("amount", e.target.value)} className="field-input !py-2 flex-1" placeholder="0.00" data-testid="ledger-form-amount" />
                        <input type="text" value={form.currency} onChange={(e) => set("currency", e.target.value.toUpperCase())} className="field-input !py-2 w-20" maxLength={4} data-testid="ledger-form-currency" />
                    </div>
                </div>
                <div>
                    <label className="label-eyebrow mb-1 block">Mode de paiement</label>
                    <input type="text" value={form.payment_method} onChange={(e) => set("payment_method", e.target.value)} className="field-input !py-2" placeholder="ex : virement, chèque, facture" maxLength={80} data-testid="ledger-form-payment-method" />
                </div>
                <div>
                    <label className="label-eyebrow mb-1 block">Période — début</label>
                    <input type="date" value={form.period_start} onChange={(e) => set("period_start", e.target.value)} className="field-input !py-2" data-testid="ledger-form-period-start" />
                </div>
                <div>
                    <label className="label-eyebrow mb-1 block">Période — fin</label>
                    <input type="date" value={form.period_end} onChange={(e) => set("period_end", e.target.value)} className="field-input !py-2" data-testid="ledger-form-period-end" />
                </div>
            </div>
            <div>
                <label className="label-eyebrow mb-1 block">Notes (facultatif)</label>
                <textarea rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} className="field-input !py-2 resize-none text-sm" placeholder="N° de bon de commande, contact comptabilité…" maxLength={2000} data-testid="ledger-form-notes" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={onCancel} className="btn-outline !text-xs">Annuler</button>
                <button type="submit" disabled={saving} className="btn-primary !text-xs" data-testid="ledger-form-submit">
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <Receipt size={14} />}
                    {isNew ? "Enregistrer" : "Mettre à jour"}
                </button>
            </div>
        </form>
    );
}


function ActivationCodesModal({ onClose }) {
    const [codes, setCodes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({ label: "", email: "", expires_at: "", notes: "" });
    const [copied, setCopied] = useState("");
    const [filter, setFilter] = useState("all"); // all | available | used | revoked

    const reload = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/activation-codes");
            setCodes(data || []);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { reload(); }, []);

    const create = async (ev) => {
        ev.preventDefault();
        setCreating(true);
        try {
            const payload = {
                label: form.label.trim() || null,
                email: form.email.trim() || null,
                expires_at: form.expires_at || null,
                notes: form.notes.trim() || null,
            };
            await api.post("/admin/activation-codes", payload);
            toast.success("Code d'activation généré ✓");
            setShowForm(false);
            setForm({ label: "", email: "", expires_at: "", notes: "" });
            await reload();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
        } finally {
            setCreating(false);
        }
    };

    const copyCode = async (code) => {
        try {
            await navigator.clipboard.writeText(code);
            setCopied(code);
            toast.success("Code copié");
            setTimeout(() => setCopied(""), 1800);
        } catch { /* ignore */ }
    };

    const revoke = async (id) => {
        if (!window.confirm("Révoquer ce code ? Il ne sera plus utilisable.")) return;
        try {
            await api.post(`/admin/activation-codes/${id}/revoke`);
            toast.success("Code révoqué");
            await reload();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Supprimer définitivement ce code ?")) return;
        try {
            await api.delete(`/admin/activation-codes/${id}`);
            toast.success("Code supprimé");
            await reload();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
        }
    };

    const filtered = codes.filter((c) => {
        if (filter === "available") return !c.used_by && !c.revoked;
        if (filter === "used") return !!c.used_by;
        if (filter === "revoked") return !!c.revoked;
        return true;
    });

    const counts = {
        all: codes.length,
        available: codes.filter((c) => !c.used_by && !c.revoked).length,
        used: codes.filter((c) => c.used_by).length,
        revoked: codes.filter((c) => c.revoked).length,
    };

    return (
        <div className="fixed inset-0 z-50 bg-ink/70 flex items-center justify-center p-2 sm:p-4" data-testid="activation-codes-modal" onClick={onClose}>
            <div className="bg-paper max-w-3xl w-full h-[92vh] sm:h-auto sm:max-h-[90vh] flex flex-col border border-rule" onClick={(ev) => ev.stopPropagation()}>
                <div className="p-4 sm:p-5 border-b border-rule flex items-start justify-between gap-3 shrink-0">
                    <div>
                        <div className="label-eyebrow">Codes d'activation Profawz</div>
                        <h2 className="h-display text-xl font-semibold mt-1">Distribution des accès aux responsables</h2>
                        <p className="text-xs text-muted2 mt-1.5 leading-relaxed">
                            Chaque code est <strong>à usage unique</strong>. Générez-en un, transmettez-le au responsable d'un lycée, et il pourra créer son compte responsable.
                        </p>
                    </div>
                    <button onClick={onClose} className="p-1.5 hover:bg-ink/5" data-testid="activation-codes-close" aria-label="Fermer"><X size={18} /></button>
                </div>

                <div className="p-3 sm:p-4 border-b border-rule bg-surface flex flex-wrap gap-2 items-center justify-between shrink-0">
                    <div className="flex flex-wrap gap-1">
                        {[
                            { k: "all", label: `Tous (${counts.all})` },
                            { k: "available", label: `Disponibles (${counts.available})` },
                            { k: "used", label: `Utilisés (${counts.used})` },
                            { k: "revoked", label: `Révoqués (${counts.revoked})` },
                        ].map((t) => (
                            <button key={t.k} type="button" onClick={() => setFilter(t.k)} className={`px-2.5 py-1 text-[11px] transition-colors ${filter === t.k ? "bg-ink text-white border border-ink" : "bg-paper border border-rule hover:border-ink/40"}`} data-testid={`activation-codes-filter-${t.k}`}>
                                {t.label}
                            </button>
                        ))}
                    </div>
                    <button onClick={() => setShowForm((v) => !v)} className="btn-primary !text-xs !px-3 !py-2" data-testid="activation-codes-new-button">
                        <Plus size={12} /> {showForm ? "Annuler" : "Générer un code"}
                    </button>
                </div>

                {showForm && (
                    <form onSubmit={create} className="p-4 border-b-2 border-orange/40 bg-orange/5 space-y-3 shrink-0" data-testid="activation-codes-form">
                        <div className="grid sm:grid-cols-2 gap-3">
                            <div>
                                <label className="label-eyebrow mb-1 block">Nom de l'établissement</label>
                                <input type="text" className="field-input !py-2" value={form.label} onChange={(e) => setForm((p) => ({ ...p, label: e.target.value }))} placeholder="ex : Lycée Sainte Famille · 2026-2027" maxLength={140} data-testid="activation-codes-form-label" />
                            </div>
                            <div>
                                <label className="label-eyebrow mb-1 block">E-mail destinataire</label>
                                <input type="email" className="field-input !py-2" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value.toLowerCase().trim() }))} placeholder="responsable@lycee.fr (optionnel)" data-testid="activation-codes-form-email" />
                            </div>
                            <div>
                                <label className="label-eyebrow mb-1 block">Date d'expiration</label>
                                <input type="date" className="field-input !py-2" value={form.expires_at} onChange={(e) => setForm((p) => ({ ...p, expires_at: e.target.value }))} data-testid="activation-codes-form-expires" />
                            </div>
                        </div>
                        <div>
                            <label className="label-eyebrow mb-1 block">Notes</label>
                            <textarea rows={2} className="field-input !py-2 resize-none text-sm" value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} placeholder="Référence facture, contact comptabilité…" maxLength={2000} data-testid="activation-codes-form-notes" />
                        </div>
                        <div className="flex justify-end">
                            <button type="submit" disabled={creating} className="btn-primary !text-xs" data-testid="activation-codes-form-submit">
                                {creating ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />}
                                Générer le code
                            </button>
                        </div>
                    </form>
                )}

                <div className="flex-1 overflow-y-auto scroll-visible min-h-0">
                    {loading ? (
                        <div className="p-10 text-center text-xs text-muted2 font-mono">Chargement…</div>
                    ) : filtered.length === 0 ? (
                        <div className="p-10 text-center text-xs text-muted2 font-mono" data-testid="activation-codes-empty">Aucun code dans cette catégorie.</div>
                    ) : filtered.map((c) => {
                        const isUsed = !!c.used_by;
                        const isRevoked = !!c.revoked;
                        const isExpired = c.expires_at && new Date(c.expires_at) < new Date();
                        const status = isUsed ? "used" : isRevoked ? "revoked" : isExpired ? "expired" : "available";
                        const statusLabel = { used: "Utilisé", revoked: "Révoqué", expired: "Expiré", available: "Disponible" }[status];
                        const statusClass = {
                            used: "bg-emerald-50 text-emerald-700 border-emerald-200",
                            revoked: "bg-red-50 text-red-700 border-red-200",
                            expired: "bg-amber-50 text-amber-700 border-amber-200",
                            available: "bg-ink text-white border-ink",
                        }[status];
                        return (
                            <div key={c.id} className="p-4 border-b border-rule hover:bg-surface" data-testid={`activation-code-${c.id}`}>
                                <div className="flex items-start gap-3 flex-wrap">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-baseline gap-2 flex-wrap">
                                            <code className="font-mono font-bold text-base">{c.code}</code>
                                            <span className={`pill border ${statusClass} !text-[10px]`}>{statusLabel}</span>
                                            <button onClick={() => copyCode(c.code)} className="text-orange hover:underline text-[11px] inline-flex items-center gap-1" data-testid={`activation-code-copy-${c.id}`}>
                                                {copied === c.code ? <><Check size={11} /> Copié</> : <><Copy size={11} /> Copier</>}
                                            </button>
                                        </div>
                                        <div className="text-[11px] font-mono text-muted2 mt-1 flex flex-wrap gap-3">
                                            {c.label && <span className="text-ink/80 font-semibold">{c.label}</span>}
                                            {c.email && <span>→ {c.email}</span>}
                                            <span>Créé le {new Date(c.created_at).toLocaleDateString("fr-FR")}</span>
                                            {c.expires_at && <span className={isExpired ? "text-red-700" : ""}>Expire le {new Date(c.expires_at).toLocaleDateString("fr-FR")}</span>}
                                            {c.used_at && <span className="text-emerald-700">Utilisé le {new Date(c.used_at).toLocaleDateString("fr-FR")}{c.used_email ? ` par ${c.used_email}` : ""}</span>}
                                        </div>
                                        {c.notes && <p className="text-xs text-ink/70 mt-2 whitespace-pre-wrap">{c.notes}</p>}
                                    </div>
                                    <div className="flex flex-wrap gap-1.5 shrink-0">
                                        {!isUsed && !isRevoked && (
                                            <button onClick={() => revoke(c.id)} className="btn-outline !text-xs !px-2 !py-1.5 !text-amber-700 !border-amber-300 hover:!bg-amber-50" title="Révoquer" data-testid={`activation-code-revoke-${c.id}`}>
                                                <Lock size={12} />
                                            </button>
                                        )}
                                        <button onClick={() => remove(c.id)} className="btn-outline !text-xs !px-2 !py-1.5 !text-destructive !border-destructive/30 hover:!bg-destructive/5" title="Supprimer" data-testid={`activation-code-delete-${c.id}`}>
                                            <Trash2 size={12} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
